import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function Account({ user, profile, onBack, onSignOut }) {
  const [inviteCode, setInviteCode] = useState(null)
  const [householdId, setHouseholdId] = useState(null)
  const [members, setMembers] = useState([])
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)
  const [switchCode, setSwitchCode] = useState('')
  const [switchError, setSwitchError] = useState(null)
  const [switchLoading, setSwitchLoading] = useState(false)

  useEffect(() => {
    fetchHouseholdInfo()
  }, [])

  async function fetchHouseholdInfo() {
    try {
      const { data: membershipRows } = await supabase
        .from('household_members')
        .select('household_id')
        .eq('user_id', user.id)
        .limit(1)
      const membership = membershipRows?.[0] ?? null
      const memErr = null
      console.log('membership:', membership, memErr)
      if (!membership) return

      setHouseholdId(membership.household_id)

      const [{ data: household, error: hhErr }, { data: memberRows, error: mrErr }] = await Promise.all([
        supabase.from('households').select('invite_code').eq('id', membership.household_id).single(),
        supabase.from('household_members').select('user_id').eq('household_id', membership.household_id),
      ])
      console.log('household:', household, hhErr)
      console.log('memberRows:', memberRows, mrErr)

      setInviteCode(household?.invite_code ?? null)

      if (memberRows) {
        const profiles = await Promise.all(
          memberRows.map(m => supabase.from('profiles').select('name').eq('id', m.user_id).maybeSingle())
        )
        setMembers(profiles.map(r => r.data).filter(Boolean))
      }
    } catch (e) {
      console.error('fetchHouseholdInfo error:', e)
    } finally {
      setLoading(false)
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(inviteCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function switchHousehold() {
    if (!switchCode.trim()) return
    setSwitchLoading(true)
    setSwitchError(null)

    const { data: newHousehold } = await supabase
      .from('households')
      .select('id')
      .eq('invite_code', switchCode.trim().toLowerCase())
      .maybeSingle()

    if (!newHousehold) {
      setSwitchError('Code not found. Double-check with the person who invited you.')
      setSwitchLoading(false)
      return
    }

    if (newHousehold.id === householdId) {
      setSwitchError("That's already your household code.")
      setSwitchLoading(false)
      return
    }

    await supabase.from('household_members').delete().eq('user_id', user.id)
    await supabase.from('household_members').insert({ user_id: user.id, household_id: newHousehold.id })
    window.location.reload()
  }

  return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Account</div>
        <div style={{ width: 44 }} />
      </div>

      <div style={styles.content}>
        <div style={styles.nameRow}>
          <div style={styles.avatar}>{(profile?.name || user.email)?.[0]?.toUpperCase()}</div>
          <div>
            <div style={styles.nameText}>{profile?.name || 'You'}</div>
            <div style={styles.emailText}>{user.email}</div>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Household members</div>
          {loading ? (
            <div style={styles.hint}>Loading…</div>
          ) : members.length === 0 ? (
            <div style={styles.hint}>Just you so far.</div>
          ) : (
            members.map((m, i) => (
              <div key={i} style={styles.memberRow}>
                <div style={styles.memberAvatar}>{m.name?.[0]?.toUpperCase()}</div>
                <div style={styles.memberName}>{m.name}</div>
              </div>
            ))
          )}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Invite someone</div>
          <div style={styles.hint}>Share this code with your partner or family member. They'll enter it when they sign up.</div>
          {inviteCode && (
            <div style={styles.codeRow}>
              <div style={styles.codeBox}>{inviteCode}</div>
              <button style={styles.copyBtn} onClick={copyCode}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          )}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Switch household</div>
          {switching ? (
            <>
              <div style={styles.hint}>Enter the invite code for the household you want to join.</div>
              <input
                style={styles.switchInput}
                placeholder="Invite code"
                value={switchCode}
                onChange={e => { setSwitchCode(e.target.value); setSwitchError(null) }}
                autoCapitalize="none"
                autoCorrect="off"
              />
              {switchError && <div style={styles.error}>{switchError}</div>}
              <div style={styles.switchBtns}>
                <button style={styles.btnGhost} onClick={() => { setSwitching(false); setSwitchCode(''); setSwitchError(null) }}>
                  Cancel
                </button>
                <button style={styles.btnPrimary} onClick={switchHousehold} disabled={switchLoading || !switchCode.trim()}>
                  {switchLoading ? 'Joining…' : 'Switch'}
                </button>
              </div>
            </>
          ) : (
            <button style={styles.switchTrigger} onClick={() => setSwitching(true)}>
              Join a different household →
            </button>
          )}
        </div>

        <button style={styles.signOutBtn} onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '430px', margin: '0 auto', minHeight: '100vh',
    background: 'white', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
  },
  navBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px 10px', flexShrink: 0,
  },
  backBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  navTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '18px', color: '#1a1a18' },
  content: { flex: 1, padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', gap: '24px' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '14px', padding: '8px 0' },
  avatar: {
    width: '48px', height: '48px', borderRadius: '50%',
    background: '#E1F5EE', color: '#0F6E56',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', fontWeight: '600', flexShrink: 0,
  },
  nameText: { fontSize: '16px', fontWeight: '500', color: '#1a1a18' },
  emailText: { fontSize: '12px', color: '#aaa', marginTop: '2px' },
  section: { display: 'flex', flexDirection: 'column', gap: '10px' },
  sectionLabel: {
    fontSize: '11px', fontWeight: '600', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  hint: { fontSize: '13px', color: '#888', lineHeight: '1.6' },
  memberRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  memberAvatar: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: '#f9f9f7', border: '0.5px solid #eee',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '500', color: '#888', flexShrink: 0,
  },
  memberName: { fontSize: '14px', color: '#1a1a18' },
  codeRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  codeBox: {
    flex: 1, padding: '12px 16px',
    background: '#f9f9f7', border: '0.5px solid #eee', borderRadius: '12px',
    fontSize: '18px', fontWeight: '500', letterSpacing: '0.12em', color: '#1a1a18',
    textAlign: 'center', fontFamily: 'monospace',
  },
  copyBtn: {
    padding: '12px 16px', background: '#1D9E75', color: 'white',
    border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
  },
  switchTrigger: {
    background: 'none', border: 'none', padding: 0,
    fontSize: '13px', color: '#1D9E75', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", textAlign: 'left',
  },
  switchInput: {
    padding: '12px 14px', borderRadius: '10px', border: '0.5px solid #ddd',
    fontSize: '14px', fontFamily: "'DM Sans', sans-serif",
    outline: 'none', background: '#f9f9f7', letterSpacing: '0.06em',
  },
  switchBtns: { display: 'flex', gap: '8px' },
  btnPrimary: {
    flex: 1, padding: '11px', background: '#1D9E75', color: 'white',
    border: 'none', borderRadius: '12px', fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  btnGhost: {
    flex: 1, padding: '11px', background: 'none', border: '0.5px solid #ddd',
    borderRadius: '12px', fontSize: '13px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  error: {
    background: '#FCEBEB', color: '#A32D2D',
    padding: '10px 14px', borderRadius: '10px', fontSize: '13px',
  },
  signOutBtn: {
    marginTop: 'auto', padding: '12px', background: 'none',
    border: '0.5px solid #eee', borderRadius: '12px',
    fontSize: '13px', color: '#aaa', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
}
